// Shared date handling for AG Grid columns (#10041).
//
// Backend grids send display strings (company_date patterns such as
// DD-MM-YYYY / DD/MM/YYYY, optionally with a HH:mm[:ss] suffix), so AG Grid's
// default string comparator ordered dates by the day characters first. Both
// the sort comparator and the agDateColumnFilter comparator in aggrid.js
// resolve cell values through parseDateCellValue so sorting and filtering
// agree on what a cell means.

// Day-first with / or - separators, matching every company_date pattern the
// backend normalizes to, plus the optional company_datetime time suffix.
const DMY_RE = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;
// Quantities like "15", "1,234" or "12.5" appear in columns whose name
// happens to contain "date" (e.g. Days to Date); they are not dates.
const NUMERIC_RE = /^-?[\d,]+(?:\.\d+)?$/;
// ISO date-only values parse as LOCAL calendar dates: new Date("2026-10-01")
// means UTC midnight, which lands on the previous day in negative-offset
// time zones and breaks date-filter equality there.
const ISO_DATE_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

// new Date() silently rolls overflowed components over (31/02 becomes
// 3 March), which would sort and filter an invalid cell as a real date.
function buildValidatedDate(year, month, day, hours, minutes, seconds) {
  const built = new Date(year, month - 1, day, hours, minutes, seconds);
  if (
    built.getFullYear() !== year
    || built.getMonth() !== month - 1
    || built.getDate() !== day
    || built.getHours() !== hours
    || built.getMinutes() !== minutes
    || built.getSeconds() !== seconds
  ) {
    return null;
  }
  return built;
}

export function parseDateCellValue(cellValue) {
  if (cellValue === null || cellValue === undefined || cellValue === '') return null;
  if (cellValue instanceof Date) {
    return isNaN(cellValue.getTime()) ? null : cellValue;
  }
  if (typeof cellValue === 'number') {
    const fromEpoch = new Date(cellValue);
    return isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }
  if (typeof cellValue !== 'string') return null;
  const s = cellValue.trim();
  if (!s || NUMERIC_RE.test(s)) return null;
  const m = s.match(DMY_RE);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    let year = parseInt(m[3], 10);
    if (year < 100) year += 2000;
    const hours = m[4] ? parseInt(m[4], 10) : 0;
    const minutes = m[5] ? parseInt(m[5], 10) : 0;
    const seconds = m[6] ? parseInt(m[6], 10) : 0;
    return buildValidatedDate(year, month, day, hours, minutes, seconds);
  }
  const iso = s.match(ISO_DATE_RE);
  if (iso) {
    return buildValidatedDate(
      parseInt(iso[1], 10),
      parseInt(iso[2], 10),
      parseInt(iso[3], 10),
      0,
      0,
      0,
    );
  }
  // MMM company formats ("05 Oct 2026", "05-Oct-2026") and ISO datetimes.
  const parsed = new Date(s);
  return isNaN(parsed.getTime()) ? null : parsed;
}

export function dateSortComparator(a, b) {
  const dateA = parseDateCellValue(a);
  const dateB = parseDateCellValue(b);
  if (dateA === null && dateB === null) {
    // Keep a deterministic order for columns the date heuristic caught by
    // name but whose values never parse (previous string-sort behavior).
    const strA = String(a ?? '');
    const strB = String(b ?? '');
    if (strA === strB) return 0;
    return strA > strB ? 1 : -1;
  }
  if (dateA === null) return -1;
  if (dateB === null) return 1;
  return dateA.getTime() - dateB.getTime();
}
